import { InsuranceData } from '@dataTypes/insurance.types'
import axios from './axiosConfig'
import { getEnv } from '@utils/env'
import { logger } from '@utils/safeLogger'
import { toApiError } from './errors/api-error'

// Constants
const BUSINESS_LAYER_API = getEnv('REACT_APP_BUSINESS_LAYER_INSURANCE', '')
const EXTERNAL_LAYER_API = getEnv('REACT_APP_EXTERNAL_LAYER_INSURANCE', '')

const headers = {
  'Content-Type': 'application/json',
}

// Types
interface ApiResponse<T> {
  statusCode: number
  message: string
  data: T
}

type InsurancePlansResponseData = InsuranceData

export type GetInsurancePlansByPatientIdResponse =
  ApiResponse<InsurancePlansResponseData>

export type CreateInsurancePlanByPatientIdResponse =
  ApiResponse<InsurancePlansResponseData>

export type DeleteInsurancePlanByIdResponse =
  ApiResponse<InsurancePlansResponseData>

export type SetPrimaryInsurancePlanByIdResponse =
  ApiResponse<InsurancePlansResponseData>

export type SetVerifiedOnInsurancePlanByIdResponse =
  ApiResponse<InsurancePlansResponseData>

// API Functions
export const GetInsurancePlansByPatientId = async (
  patientId: string,
): Promise<GetInsurancePlansByPatientIdResponse> => {
  try {
    const response = await axios.get<GetInsurancePlansByPatientIdResponse>(
      `${BUSINESS_LAYER_API}/api/v1/insurance/${patientId}`,
      {
        headers,
      },
    )

    return response.data
  } catch (e: unknown) {
    logger.error('Error in GetInsurancePlansByPatientId:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

// API Functions
export const GetPatientEligibilityDetails = async (
  formData: any,
): Promise<any> => {
  try {
    const response = await axios.post<any>(
      `${EXTERNAL_LAYER_API}/api/v1/external/insurance/eligibility`,
      formData,
      {
        headers,
      },
    )

    return response.data
  } catch (e: unknown) {
    logger.error('Error in GetPatientEligibilityDetails:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

// API Functions
export const CreateInsurancePlanByPatientId = async (
  patientId: string,
  insuranceData: any,
): Promise<any> => {
  try {
    const response = await axios.post<CreateInsurancePlanByPatientIdResponse>(
      `${BUSINESS_LAYER_API}/api/v1/insurance/${patientId}`,
      insuranceData,
      {
        headers,
      },
    )

    return response.data
  } catch (e: unknown) {
    logger.error('Error in CreateInsurancePlanByPatientId:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

// API Functions
export const DeleteInsurancePlanById = async (
  insurancePlanId: string,
): Promise<any> => {
  try {
    const response = await axios.delete<DeleteInsurancePlanByIdResponse>(
      `${BUSINESS_LAYER_API}/api/v1/insurance/${insurancePlanId}`,
      {
        headers,
      },
    )

    return response.data
  } catch (e: unknown) {
    logger.error('Error in DeleteInsurancePlanById:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const SetPrimaryInsurancePlanById = async (
  insurancePlanId: string,
): Promise<any> => {
  try {
    const response = await axios.put<SetPrimaryInsurancePlanByIdResponse>(
      `${BUSINESS_LAYER_API}/api/v1/insurance/${insurancePlanId}`,
      {
        headers,
      },
    )

    return response.data
  } catch (e: unknown) {
    logger.error('Error in SetPrimaryInsurancePlanById:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const SetVerifiedOnInsurancePlanById = async (
  insurancePlanId: string,
): Promise<any> => {
  try {
    const response = await axios.put<SetVerifiedOnInsurancePlanByIdResponse>(
      `${BUSINESS_LAYER_API}/api/v1/insurance/${insurancePlanId}/verified`,
      {
        headers,
      },
    )

    return response.data
  } catch (e: unknown) {
    logger.error('Error in SetVerifiedOnInsurancePlanById:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}
